import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, } from "@mui/material";
import MainLayout from "../../layouts/main/main_layout";
import { useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { getFMPortalDetails, getFMPortalNames } from "../../services/fm_portal/fm_portal";
import { formatHourLabel } from "./utils";

interface HourlyReportDetailsLocationState {
  choiceId?: string;
  date?: string;
  surveyName?: string;
}

const extractApiData = (value: any) => {
  if (!value) return {};

  return value?.data?.data?.data ?? value?.data?.data ?? value?.data ?? value;
};

const normalizeList = (value: any) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.fr_details)) return value.fr_details;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

export default function HourlyReportDetails() {
  const location = useLocation();
  const state = (location.state || {}) as HourlyReportDetailsLocationState;
  const persistedDate =
    typeof window !== "undefined"
      ? sessionStorage.getItem("fmPortalSelectedDate") || ""
      : "";
  const selectedDate = state.date || persistedDate;
  const [fr, setFr] = useState("");
  const [showAllFrs, setShowAllFrs] = useState(false);

  const hasChoiceSelection = Boolean(state.choiceId && selectedDate);

  const { data: namesResponse, isLoading: isNamesLoading } = useQuery(
    ["fmPortalNames", state.choiceId, state.date],
    () => getFMPortalNames(state.choiceId || "", selectedDate),
    {
      enabled: hasChoiceSelection,
      staleTime: 0,
    }
  );

  const namesPayload = useMemo(
    () => extractApiData(namesResponse),
    [namesResponse]
  );

  const frOptions = useMemo(
    () => normalizeList(namesPayload?.list ?? namesPayload),
    [namesPayload]
  );

  const { data: detailsResponse, isLoading: isDetailsLoading } = useQuery(
    ["fmPortalDetails", state.choiceId, state.date, fr, showAllFrs],
    () =>
      getFMPortalDetails(
        state.choiceId || "",
        selectedDate,
        showAllFrs ? "" : fr
      ),
    {
      enabled: Boolean(
        state.choiceId &&
        selectedDate &&
        (showAllFrs || fr)
      ),
      staleTime: 0,
    }
  );

  const detailsPayload = useMemo(
    () => extractApiData(detailsResponse),
    [detailsResponse]
  );

  const detailsList = useMemo(
    () => normalizeList(detailsPayload?.list ?? detailsPayload),
    [detailsPayload]
  );

  const displayFrDetails = useMemo(() => {
    if (showAllFrs) {
      return detailsList;
    }

    if (!fr) return [];

    return detailsList;
  }, [detailsList, showAllFrs, fr]);

  const hourKeys = useMemo(() => {
    const firstSurveyor = displayFrDetails?.[0]?.surveyors?.[0];

    return Object.keys(firstSurveyor?.hourly_counts || {});
  }, [displayFrDetails]);



  return (
    <MainLayout>
      <Box px={2} py={3}>
        <Typography variant="h6" fontWeight={700} mb={0.5}>
          Hourly Report Details
        </Typography>
        <Divider
          sx={{
            display: "flex",
            opacity: 0.5,
            borderBottom: "1px dotted #7C797C",
            mt: 2,
            mb: 3,
          }}
        />

        <Box maxWidth="100%" mx="auto">
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={4}>
            <Card
              sx={{
                width: "100%",
                minHeight: 150,
                px: 3,
                py: 3,
                boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" fontWeight={700} mb={0.5}>
                  {state.surveyName || "Selected Survey"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={1}
                >
                  Date: {selectedDate || "-"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Project ID: {state.choiceId || "-"}
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                width: "100%",
                minHeight: 150,
                px: 3,
                py: 3,
                boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 0 }}>

                {isNamesLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    py={2}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    {frOptions.length ? (
                      <>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Choose FR"
                          value={fr}
                          onChange={(e) => setFr(e.target.value)}
                        >
                          {frOptions.map(
                            (item: any, index: number) => {
                              const itemValue = item?.fr_name;

                              return (
                                <MenuItem
                                  key={`${itemValue}-${index}`}
                                  value={itemValue}
                                >
                                  {itemValue}
                                </MenuItem>
                              );
                            }
                          )}
                        </TextField>
                        <Box display="flex" gap={2} mt={1}>
                          <Button
                            variant={!showAllFrs ? "contained" : "outlined"}
                            onClick={() => setShowAllFrs(false)}
                            disabled={!fr}
                          >
                            Selected FR
                          </Button>

                          <Button
                            variant={showAllFrs ? "contained" : "outlined"}
                            onClick={() => {
                              setShowAllFrs(true);
                              setFr("");
                            }}
                          >
                            Check For All FRs
                          </Button>
                        </Box>
                      </>

                    ) : (
                      <>
                        <Alert severity="info" >
                          No FR names found for this survey and
                          date.
                        </Alert>
                        <Button
                          variant={showAllFrs ? "contained" : "outlined"}
                          onClick={() => {
                            setShowAllFrs(true);
                            setFr("");
                          }}
                        // sx={{mt:2}}
                        >
                          Check For All FRs
                        </Button>
                      </>

                    )}

                  </>
                )}
              </CardContent>
            </Card>
          </Box>

          {isDetailsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              py={4}
            >
              <CircularProgress />
            </Box>
          ) : (
            displayFrDetails.map((frItem: any) => (
              <Card
                key={frItem.fr_name}
                sx={{
                  width: "100%",
                  px: 3,
                  py: 3,
                  boxShadow:
                    "0px 4px 16px rgba(0, 0, 0, 0.08)",
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={700}
                    >
                      FR Name: {frItem.fr_name}
                    </Typography>

                    <Typography
                      variant="body1"
                      fontWeight={600}
                    >
                      Total Surveyors:{" "}
                      {frItem.total_surveyors_count}
                    </Typography>
                  </Box>

                  <Paper sx={{ overflowX: "auto" }}>
                    <Table
                      size="small"
                      sx={{
                        "& .MuiTableCell-root": {
                          py: 0.5,
                          px: 1,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableHead sx={{
                        "& .MuiTableCell-root": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          borderRight: "2px solid rgba(255,255,255,0.2)",
                          py: 1,
                          px: 1.5,
                        },
                        "& .MuiTableCell-root:last-child": {
                          borderRight: "none",
                        },
                      }}>
                        <TableRow >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Surveyor Name
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Surveyor ID
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Contact Number
                          </TableCell>

                          {hourKeys.map((hour) => (
                            <TableCell
                              key={hour}
                              align="center"
                              sx={{
                                fontWeight: 700,
                                minWidth: 70,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatHourLabel(hour)}
                            </TableCell>
                          ))}

                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>


                      <TableBody>
                        {frItem.surveyors?.length ? (
                          <>
                            {(() => {
                              const columnTotals = hourKeys.reduce(
                                (acc: Record<string, number>, hour) => {
                                  acc[hour] = frItem.surveyors.reduce(
                                    (sum: number, surveyor: any) =>
                                      sum + (surveyor?.hourly_counts?.[hour] ?? 0),
                                    0
                                  );

                                  return acc;
                                },
                                {}
                              );

                              const grandTotal = frItem.surveyors.reduce(
                                (sum: number, surveyor: any) =>
                                  sum + (surveyor.total_hours ?? 0),
                                0
                              );

                              return (
                                <>
                                  {frItem.surveyors.map((surveyor: any) => (
                                    <TableRow key={surveyor.surveyor_id}>
                                      <TableCell>
                                        {surveyor.surveyor_name}
                                      </TableCell>

                                      <TableCell>
                                        {surveyor.surveyor_id}
                                      </TableCell>

                                      <TableCell>
                                        {surveyor.surveyor_mobile_number}
                                      </TableCell>

                                      {hourKeys.map((hour) => (
                                        <TableCell
                                          key={hour}
                                          align="center"
                                        >
                                          {surveyor?.hourly_counts?.[hour] ?? 0}
                                        </TableCell>
                                      ))}

                                      <TableCell
                                        align="center"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {surveyor.total_hours}
                                      </TableCell>
                                    </TableRow>
                                  ))}

                                  {/* Grand Total Row for this FR */}
                                  <TableRow
                                    sx={{
                                      backgroundColor: "primary.main",
                                      "& td": {
                                        color: "primary.contrastText",
                                        fontWeight: 700,
                                      },
                                    }}
                                  >
                                    <TableCell
                                      colSpan={3}
                                      align="center"
                                    >
                                      Grand Total
                                    </TableCell>

                                    {hourKeys.map((hour) => (
                                      <TableCell
                                        key={hour}
                                        align="center"
                                      >
                                        {columnTotals[hour]}
                                      </TableCell>
                                    ))}

                                    <TableCell align="center">
                                      {grandTotal}
                                    </TableCell>
                                  </TableRow>
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={hourKeys.length + 4}
                              align="center"
                            >
                              No surveyor data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Box>
    </MainLayout>
  );
}

